<script setup lang="ts">
import { ref, onMounted, computed, provide } from 'vue'

import { BlockContainer, useDataLayer } from '@toni/brand-ui'
import { BauerProductCard, ProductCardLayout, PurchaseOptions } from '@toni/commerce-api-types'

import ProductCardFull from './ProductCardFull.vue'
import ProductCardSimple from './ProductCardSimple.vue'

const props = withDefaults(defineProps<{
  bauerProductCard: BauerProductCard,
  purchaseOptions?: PurchaseOptions,
  layout?: ProductCardLayout,
  contentBlocks?: string[],
  productCardIndex?: number
}>(), {
  layout: 'FULL',
  purchaseOptions: undefined,
  contentBlocks: undefined,
  productCardIndex: undefined
})

const getProductCardRelativePosition = (index: number | undefined, contentBlocks: string[] | undefined): string => {
  if (!contentBlocks || !index || index === -1) {
    return ''
  }

  return `${((index + 1) * 100 / contentBlocks.length).toFixed(0)}%`
}

const getProductCardPosition = (index: number | undefined, array: number[]): number => {
  if (!array || !index || index === -1) {
    return 0
  }
  return array.indexOf(index) + 1
}

const productCardsIndex = computed(() => props.contentBlocks ? props.contentBlocks.map((paragraph, index) => paragraph === 'productCard' ? index : -1).filter((index) => index !== -1) : [])
const productCardPosition = `${computed(() => getProductCardPosition(props.productCardIndex, productCardsIndex.value)).value}/${productCardsIndex.value.length}`
const productCardRelativePosition = computed(() => getProductCardRelativePosition(props.productCardIndex, props.contentBlocks))
const productCardScrollTrackingRef = ref(null)
let productCardInView = false

onMounted(() => {
  if (props.contentBlocks && props.bauerProductCard.bauerProductCardId && productCardPosition && productCardRelativePosition) {
    const dataLayer = useDataLayer()

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting && productCardInView === false) {
          productCardInView = true

          const productCardTrackingData = {
            event: 'product_card_impression',
            product_card_id: props.bauerProductCard.bauerProductCardId,
            placement: 'product_card',
            product_card_position: productCardPosition,
            product_card_rel_position: productCardRelativePosition.value
          }

          dataLayer.push(productCardTrackingData)
        }
      },
      { threshold: 1.0 }
    )

    if (productCardScrollTrackingRef.value) {
      observer.observe(productCardScrollTrackingRef.value)
    }
  }
})

provide('productCardTracking', {
  product_card_id: computed(() => props.bauerProductCard.bauerProductCardId).value,
  product_card_position: productCardPosition,
  product_card_rel_position: productCardRelativePosition.value
})
</script>

<template>
  <BlockContainer>
    <ProductCardFull v-if="layout === 'FULL'"
                     :product-card="bauerProductCard"
                     :purchase-options="purchaseOptions"
                     :embedded="true" />
    <ProductCardSimple v-else
                       :product-card="bauerProductCard"
                       :purchase-options="purchaseOptions"
                       :embedded="false" />
    <div v-if="contentBlocks && contentBlocks.length > 0"
         ref="productCardScrollTrackingRef" />
  </BlockContainer>
</template>
